import type { RouteRecordRaw } from 'vue-router'
import type { RouteConfig } from './types'

function signupRouteMeta(requiresAuth: boolean) {
  return { requiresAuth, hideNavbar: true, isSignup: true, isCustomer: true }
}

function clinicalBookingRouteMeta() {
  return {
    requiresAuth: true,
    hideNavbar: true,
    isCustomer: true,
    isClinicalBooking: true,
    consultation: true,
    skipTransition: true,
    showFilters: false,
    showInNavbar: false,
  }
}

const SignupView = () => import('@/views/SignupView.vue')
const WelcomeScreen = () => import('@/components/signup/screens/WelcomeScreen.vue')
const CaregiverSupportScreen = () =>
  import('@/components/signup/screens/CaregiverSupportScreen.vue')
const CancerJourneyScreen = () => import('@/components/signup/screens/CancerJourneyScreen.vue')
const TrustScreen = () => import('@/components/signup/screens/TrustScreen.vue')
const ReferralSelectionScreen = () =>
  import('@/components/signup/screens/ReferralSelectionScreen.vue')
const CreateAccountScreen = () => import('@/components/signup/screens/CreateAccountScreen.vue')
const EmailAlreadyExistsScreen = () =>
  import('@/components/signup/screens/EmailAlreadyExistsScreen.vue')
const UserAssessmentScreen = () => import('@/components/signup/screens/UserAssessmentScreen.vue')
const AlmostThereLoaderScreen = () =>
  import('@/components/signup/screens/AlmostThereLoaderScreen.vue')
const FreeTrialLoaderScreen = () => import('@/components/signup/screens/FreeTrialLoaderScreen.vue')
const ClassSelectionScreen = () => import('@/components/signup/screens/ClassSelectionScreen.vue')
const PatientInformationScreen = () =>
  import('@/components/signup/screens/PatientInformationScreen.vue')
const SignupInsuranceCategory = () => import('@/components/signup/SignupInsuranceCategory.vue')
const SignupClinicalConsultationInterest = () =>
  import('@/components/signup/SignupClinicalConsultationInterest.vue')
const ClinicalBookingInSignup = () => import('@/components/signup/ClinicalBookingInSignup.vue')
const PlanSelectionRouteScreen = () =>
  import('@/components/signup/screens/PlanSelectionRouteScreen.vue')
const StripeCheckout = () => import('@/components/signup/StripeCheckout.vue')
const ThankYouForSubscribing = () => import('@/components/signup/ThankYouForSubscribing.vue')
const ScheduleConsultation = () => import('@/components/signup/ScheduleConsultation.vue')
const SponsorMessage = () => import('@/components/signup/SponsorMessage.vue')
const SignupCompletedMessage = () => import('@/components/signup/SignupCompletedMessage.vue')

type RouteNames =
  | 'GetStarted'
  | 'Caregiver'
  | 'LeadQuestion'
  | 'LeadMotivation'
  | 'ReferralSelection'
  | 'CreateAccount'
  | 'EmailAlreadyExists'
  | 'UserAssessment'
  | 'AlmostThere'
  | 'SponsorScreen'
  | 'ClassSelection'
  | 'PatientInformation'
  | 'InsuranceCategory'
  | 'ClinicalConsultationInterest'
  | 'ConsultationBookingIntro'
  | 'ConsultationBookingExistingConsultation'
  | 'ConsultationBookingAppointmentCancelled'
  | 'ConsultationBookingReservationExpired'
  | 'ConsultationBookingUnsupportedState'
  | 'ConsultationBookingProfile'
  | 'ConsultationBookingInsuranceCategory'
  | 'ConsultationBookingCashPayEligible'
  | 'ConsultationBookingInsuranceOnFile'
  | 'ConsultationBookingInsuranceDetails'
  | 'ConsultationBookingInsuranceVerified'
  | 'ConsultationBookingSlotSelection'
  | 'ConsultationBookingHardVerification'
  | 'ConsultationBookingPreparingVisit'
  | 'ConsultationBookingPayment'
  | 'ConsultationBookingProvisioning'
  | 'ConsultationBookingConfirmed'
  | 'ConsultationBookingLoading'
  | 'FreeTrialLoader'
  | 'SignupCompletedMessage'
  | 'PlanSelection'
  | 'StripeCheckout'
  | 'ThankYouForSubscribing'
  | 'ScheduleConsultation'

export const SignupPages: RouteConfig<RouteNames> = {
  GetStarted: {
    path: '/get-started',
    name: 'get-started',
    component: WelcomeScreen,
    meta: signupRouteMeta(false),
  },
  Caregiver: {
    path: '/caregiver',
    name: 'caregiver',
    component: CaregiverSupportScreen,
    meta: signupRouteMeta(false),
  },

  LeadQuestion: {
    path: '/lead-question',
    name: 'lead-question',
    component: CancerJourneyScreen,
    meta: signupRouteMeta(false),
  },
  LeadMotivation: {
    path: '/lead-motivation',
    name: 'lead-motivation',
    component: TrustScreen,
    meta: signupRouteMeta(false),
  },
  ReferralSelection: {
    path: '/referral-selection',
    name: 'referral-selection',
    component: ReferralSelectionScreen,
    meta: signupRouteMeta(false),
  },
  CreateAccount: {
    path: '/create-account',
    name: 'create-account',
    component: CreateAccountScreen,
    meta: signupRouteMeta(false),
  },
  EmailAlreadyExists: {
    path: '/email-already-exists',
    name: 'email-already-exists',
    component: EmailAlreadyExistsScreen,
    meta: signupRouteMeta(false),
  },
  UserAssessment: {
    path: '/user-assessment',
    name: 'user-assessment',
    component: UserAssessmentScreen,
    meta: signupRouteMeta(true),
  },
  AlmostThere: {
    path: '/almost-there',
    name: 'almost-there',
    component: AlmostThereLoaderScreen,
    meta: signupRouteMeta(true),
  },
  SponsorScreen: {
    path: '/sponsor-message',
    name: 'sponsor-screen',
    component: SponsorMessage,
    meta: signupRouteMeta(true),
  },
  ClassSelection: {
    path: '/class-selection',
    name: 'class-selection',
    component: ClassSelectionScreen,
    meta: signupRouteMeta(true),
  },
  PatientInformation: {
    path: '/patient-information',
    name: 'patient-information',
    component: PatientInformationScreen,
    meta: signupRouteMeta(true),
  },
  InsuranceCategory: {
    path: '/insurance-category',
    name: 'insurance-category',
    component: SignupInsuranceCategory,
    meta: signupRouteMeta(true),
  },
  ClinicalConsultationInterest: {
    path: '/clinical-consultation-interest',
    name: 'clinical-consultation-interest',
    component: SignupClinicalConsultationInterest,
    meta: signupRouteMeta(true),
  },
  ConsultationBookingIntro: {
    path: '/consultation-booking/intro',
    name: 'consultation-booking-intro',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingExistingConsultation: {
    path: '/consultation-booking/existing-consultation',
    name: 'consultation-booking-existing-consultation',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingAppointmentCancelled: {
    path: '/consultation-booking/appointment-cancelled',
    name: 'consultation-booking-appointment-cancelled',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingReservationExpired: {
    path: '/consultation-booking/reservation-expired',
    name: 'consultation-booking-reservation-expired',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingUnsupportedState: {
    path: '/consultation-booking/unsupported-state',
    name: 'consultation-booking-unsupported-state',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingProfile: {
    path: '/consultation-booking/profile',
    name: 'consultation-booking-profile',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingInsuranceCategory: {
    path: '/consultation-booking/insurance-category',
    name: 'consultation-booking-insurance-category',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingCashPayEligible: {
    path: '/consultation-booking/cash-pay-eligible',
    name: 'consultation-booking-cash-pay-eligible',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingInsuranceOnFile: {
    path: '/consultation-booking/insurance-on-file',
    name: 'consultation-booking-insurance-on-file',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingInsuranceDetails: {
    path: '/consultation-booking/insurance-details',
    name: 'consultation-booking-insurance-details',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingInsuranceVerified: {
    path: '/consultation-booking/insurance-verified',
    name: 'consultation-booking-insurance-verified',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingSlotSelection: {
    path: '/consultation-booking/slot-selection',
    name: 'consultation-booking-slot-selection',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingHardVerification: {
    path: '/consultation-booking/hard-verification',
    name: 'consultation-booking-hard-verification',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingPreparingVisit: {
    path: '/consultation-booking/preparing-visit',
    name: 'consultation-booking-preparing-visit',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingPayment: {
    path: '/consultation-booking/payment',
    name: 'consultation-booking-payment',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingProvisioning: {
    path: '/consultation-booking/provisioning',
    name: 'consultation-booking-provisioning',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingConfirmed: {
    path: '/consultation-booking/confirmed',
    name: 'consultation-booking-confirmed',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  ConsultationBookingLoading: {
    path: '/consultation-booking/loading',
    name: 'consultation-booking-loading',
    component: ClinicalBookingInSignup,
    meta: clinicalBookingRouteMeta(),
  },
  FreeTrialLoader: {
    path: '/free-trial-loader',
    name: 'free-trial-loader',
    component: FreeTrialLoaderScreen,
    meta: signupRouteMeta(true),
  },
  SignupCompletedMessage: {
    path: '/signup-completed-message',
    name: 'signup-completed-message',
    component: SignupCompletedMessage,
    meta: signupRouteMeta(true),
  },
  PlanSelection: {
    path: '/plan-selection',
    name: 'plan-selection',
    component: PlanSelectionRouteScreen,
    meta: signupRouteMeta(true),
  },
  StripeCheckout: {
    path: '/stripe-checkout',
    name: 'stripe-checkout',
    component: StripeCheckout,
    meta: signupRouteMeta(true),
  },
  ThankYouForSubscribing: {
    path: '/thank-you-for-subscribing',
    name: 'thank-you-for-subscribing',
    component: ThankYouForSubscribing,
    meta: signupRouteMeta(true),
  },
  ScheduleConsultation: {
    path: '/schedule-consultation',
    name: 'schedule-consultation',
    component: ScheduleConsultation,
    meta: signupRouteMeta(true),
  },
}

export const SIGNUP_ROUTES_GROUP: RouteRecordRaw = {
  path: '/signup1',
  name: 'signup1',
  component: SignupView,
  children: Object.values(SignupPages) as RouteRecordRaw[],
}
